import React, { useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Autocomplete,
  Avatar,
  Paper,
  Chip,
  InputAdornment
} from '@mui/material';
import {
  Business as BusinessIcon,
  Language as PublicIcon,
  MoneyIcon,
  Assignment as AssignmentIcon
} from '@mui/icons-material';

// Import Settings Context for real-time data
import { useSettings } from '../contexts/SettingsContext';

const ForfaitsDemo = () => {
  const { settings, formatCurrency, calculateTotalWithTax } = useSettings();
  const [openDialog, setOpenDialog] = useState(false);
  const [formData, setFormData] = useState({
    nom: 'Circuit Découverte Istanbul',
    destination: 'Istanbul, Turquie',
    pays: 'Turquie',
    prix: 92500,
    prixOriginal: 110000,
    fournisseurId: 'FOUR001'
  });

  // Enhanced suppliers data
  const sampleSuppliers = [
    {
      id: 'FOUR001',
      nom: 'Turkish Airlines Tours',
      pays: 'Turquie',
      specialite: 'Vols et circuits turcs',
      avatar: 'https://ui-avatars.com/api/?name=Turkish+Airlines&background=e74c3c&color=fff'
    },
    {
      id: 'FOUR002', 
      nom: 'Emirates Premium Travel',
      pays: 'Émirats Arabes Unis',
      specialite: 'Séjours luxe Dubai',
      avatar: 'https://ui-avatars.com/api/?name=Emirates&background=c0392b&color=fff'
    },
    {
      id: 'FOUR003',
      nom: 'Al Masjid Travel',
      pays: 'Arabie Saoudite',
      specialite: 'Pèlerinages religieux',
      avatar: 'https://ui-avatars.com/api/?name=Al+Masjid&background=27ae60&color=fff'
    }
  ];

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" sx={{ fontWeight: 700, mb: 2, color: 'primary.main' }}>
          🔗 Demo Forfaits Interconnectés
        </Typography>
        
        {/* Status Banner */}
        <Paper sx={{ p: 2, bgcolor: 'rgba(76, 175, 80, 0.04)', border: '1px solid rgba(76, 175, 80, 0.2)', borderRadius: 2, mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <AssignmentIcon sx={{ color: 'success.main' }} />
              <Typography variant="body2" sx={{ fontWeight: 600, color: 'success.main' }}>
                ✅ Système Interconnecté Opérationnel
              </Typography>
            </Box>
            <Typography variant="caption" color="text.secondary">
              💱 Devise Active: {settings.defaultCurrency}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              📊 Taux de Taxe: {settings.taxRate}%
            </Typography>
            <Typography variant="caption" color="text.secondary">
              🏪 {sampleSuppliers.length} Fournisseurs Connectés
            </Typography>
          </Box>
        </Paper>

        <Button 
          variant="contained" 
          onClick={() => setOpenDialog(true)}
          sx={{ 
            background: 'linear-gradient(135deg, #1e40af 0%, #7c3aed 100%)',
            borderRadius: 2,
            px: 4,
            py: 1.5
          }}
        >
          🚀 Tester Forfait Interconnecté
        </Button>
      </Box>

      {/* Demo Dialog with Full Interconnection */}
      <Dialog 
        open={openDialog} 
        onClose={() => setOpenDialog(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            background: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(20px)'
          }
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Typography variant="h5" sx={{ fontWeight: 600 }}>
            ✨ Nouveau Forfait Interconnecté
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Toutes les données sont synchronisées en temps réel
          </Typography>
        </DialogTitle>
        
        <DialogContent>
          <Grid container spacing={3} sx={{ mt: 1 }}>
            {/* Basic Info */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Nom du forfait"
                value={formData.nom}
                onChange={(e) => setFormData({...formData, nom: e.target.value})}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Destination"
                value={formData.destination}
                onChange={(e) => setFormData({...formData, destination: e.target.value})}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
            </Grid>

            {/* Enhanced Supplier Selection */}
            <Grid item xs={12} md={6}>
              <Autocomplete
                options={sampleSuppliers}
                getOptionLabel={(option) => option.nom}
                value={sampleSuppliers.find(s => s.id === formData.fournisseurId) || null}
                onChange={(event, newValue) => {
                  setFormData({ ...formData, fournisseurId: newValue ? newValue.id : '' });
                }}
                renderOption={(props, option) => (
                  <Box component="li" {...props} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Avatar src={option.avatar} sx={{ width: 32, height: 32 }} />
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {option.nom}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {option.pays} • {option.specialite}
                      </Typography>
                    </Box>
                  </Box>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="🏪 Fournisseur Partenaire"
                    placeholder="Sélectionner un fournisseur..."
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <InputAdornment position="start">
                          <BusinessIcon />
                        </InputAdornment>
                      ),
                    }}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                )}
                fullWidth
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Pays de destination"
                value={formData.pays}
                onChange={(e) => setFormData({...formData, pays: e.target.value})}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PublicIcon />
                    </InputAdornment>
                  ),
                }}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
            </Grid>

            {/* Pricing */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Prix (DZD)"
                type="number"
                value={formData.prix}
                onChange={(e) => setFormData({...formData, prix: parseFloat(e.target.value)})}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Prix original (DZD)"
                type="number"
                value={formData.prixOriginal}
                onChange={(e) => setFormData({...formData, prixOriginal: parseFloat(e.target.value)})}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
            </Grid>

            {/* Real-time Pricing Calculator */}
            {formData.prix > 0 && (
              <Grid item xs={12}>
                <Paper sx={{ p: 3, bgcolor: 'rgba(25, 118, 210, 0.04)', borderRadius: 2 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2, color: 'primary.main' }}>
                    💰 Calculateur de Prix en Temps Réel
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={3}>
                      <Box sx={{ textAlign: 'center' }}>
                        <Typography variant="caption" color="text.secondary">Prix HT</Typography>
                        <Typography variant="h6" sx={{ fontWeight: 700 }}>
                          {formatCurrency(formData.prix)}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <Box sx={{ textAlign: 'center' }}>
                        <Typography variant="caption" color="text.secondary">
                          Taxes ({settings.taxRate}%)
                        </Typography>
                        <Typography variant="h6" sx={{ fontWeight: 700, color: 'warning.main' }}>
                          {formatCurrency(formData.prix * (settings.taxRate / 100))}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <Box sx={{ textAlign: 'center' }}>
                        <Typography variant="caption" color="text.secondary">Prix TTC Final</Typography>
                        <Typography variant="h6" sx={{ fontWeight: 700, color: 'success.main' }}>
                          {formatCurrency(calculateTotalWithTax(formData.prix))}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <Box sx={{ textAlign: 'center' }}>
                        <Typography variant="caption" color="text.secondary">Réduction</Typography>
                        <Typography variant="h6" sx={{ fontWeight: 700, color: 'error.main' }}>
                          {formData.prixOriginal > 0 
                            ? `${Math.round(((formData.prixOriginal - formData.prix) / formData.prixOriginal) * 100)}%`
                            : '0%'
                          }
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>
            )}

            {/* Interconnection Info */}
            <Grid item xs={12}>
              <Paper sx={{ p: 2, bgcolor: 'rgba(76, 175, 80, 0.04)', borderRadius: 2 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: 'success.main' }}>
                  🔗 Données Interconnectées
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  <Chip label="Visible dans Réservations" size="small" color="primary" />
                  <Chip label="Fournisseur Synchronisé" size="small" color="secondary" />
                  <Chip label="Prix Temps Réel" size="small" color="success" />
                  <Chip label="Settings Intégrés" size="small" color="warning" />
                </Box>
              </Paper>
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions sx={{ p: 3, pt: 1 }}>
          <Button onClick={() => setOpenDialog(false)} sx={{ borderRadius: 2 }}>
            Annuler
          </Button>
          <Button 
            variant="contained"
            sx={{ 
              borderRadius: 2,
              background: 'linear-gradient(135deg, #1e40af 0%, #7c3aed 100%)'
            }}
          >
            ✅ Créer Forfait Interconnecté
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ForfaitsDemo; 